"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  TextArea,
  RememberedTextInput,
  RememberedNamesNote,
  CheckboxRow,
  NumberInput,
  GuardedPrintButton,
  CompletionPanel,
  CopyButton,
  PrintFooter,
  trackToolResult,
} from "@/components/tools/ui";

/* F10 notification drafter.

   Under CDM 2015 (Regulation 6) a project must be notified to HSE before the
   construction phase begins where the construction work is scheduled to:
     - last longer than 30 working days AND have more than 20 workers working
       simultaneously at any point, OR
     - exceed 500 person-days.
   (Thresholds and the notifiability framing match the CDM duty checker — verified
   against the rams-knowledge bank, quotable HSE source, 2026-06-06. Logic is
   re-stated here, not imported.)

   Where notifiable, Schedule 1 specifies EXACTLY 15 particulars that the
   notification must contain. The 15 items below are quoted verbatim from
   Schedule 1 of the Construction (Design and Management) Regulations 2015
   (legislation.gov.uk, OGL — quotable). This tool ASSEMBLES those particulars so
   the user can paste them into HSE's official online F10 service. The notification
   itself is made to HSE — this only drafts it; it never submits anything. */

const F10_SERVICE_URL = "https://extranet.hse.gov.uk/lfserver/external/f10";

function formatLong(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

/** A drafted F10 particular: its Schedule 1 heading and the assembled value. */
interface Particular {
  /** Short label used in the form + completion panel. */
  label: string;
  /** The Schedule 1 heading, quoted, used in the printed/copied draft. */
  scheduleText: string;
  /** Assembled value to paste into the F10. */
  value: string;
  /** Whether this particular is complete enough to count as done. */
  done: boolean;
}

export function F10NotificationDrafter() {
  // ── Notifiability test (reg 6) ──────────────────────────────────────────
  const [bigOver30, setBigOver30] = useState(false);
  const [over500, setOver500] = useState(false);
  const [pdWorkers, setPdWorkers] = useState("");
  const [pdDays, setPdDays] = useState("");

  const personDays = useMemo(() => {
    const w = parseFloat(pdWorkers);
    const d = parseFloat(pdDays);
    if (!isFinite(w) || w <= 0 || !isFinite(d) || d <= 0) return null;
    return Math.round(w * d);
  }, [pdWorkers, pdDays]);

  const notifiable = bigOver30 || over500;

  // ── Schedule 1 particulars ───────────────────────────────────────────────
  // 1. Date of forwarding
  const [dateForwarded, setDateForwarded] = useState("");
  // 2. Site address / location
  const [siteAddress, setSiteAddress] = useState("");
  // 3. Local authority
  const [localAuthority, setLocalAuthority] = useState("");
  // 4. Project / work description
  const [projectDescription, setProjectDescription] = useState("");
  // 5. Client
  const [clientName, setClientName] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  // 6. Principal designer
  const [pdName, setPdName] = useState("");
  const [pdAddress, setPdAddress] = useState("");
  const [pdPhone, setPdPhone] = useState("");
  const [pdEmail, setPdEmail] = useState("");
  // 7. Principal contractor
  const [pcName, setPcName] = useState("");
  const [pcAddress, setPcAddress] = useState("");
  const [pcPhone, setPcPhone] = useState("");
  const [pcEmail, setPcEmail] = useState("");
  // 8. Planned start of construction phase
  const [startDate, setStartDate] = useState("");
  // 9. Time allocated by client (reg 4(1))
  const [timeAllocated, setTimeAllocated] = useState("");
  // 10. Planned duration of construction phase
  const [duration, setDuration] = useState("");
  // 11. Estimated max people at work on site
  const [maxPeople, setMaxPeople] = useState("");
  // 12. Planned number of contractors
  const [numContractors, setNumContractors] = useState("");
  // 13. Contractors already appointed (name & address)
  const [appointedContractors, setAppointedContractors] = useState("");
  // 14. Designers already appointed (name & address)
  const [appointedDesigners, setAppointedDesigners] = useState("");
  // 15. Client declaration
  const [clientDeclared, setClientDeclared] = useState(false);

  const resultTracked = useRef(false);

  // Assemble a contact block from name / address / phone / email parts.
  const contactBlock = (name: string, address: string, phone: string, email: string) =>
    [
      name && `Name: ${name}`,
      address && `Address: ${address}`,
      phone && `Tel: ${phone}`,
      email && `Email: ${email}`,
    ]
      .filter(Boolean)
      .join("\n");

  const clientContact = contactBlock(clientName, clientAddress, clientPhone, clientEmail);
  const pdContact = contactBlock(pdName, pdAddress, pdPhone, pdEmail);
  const pcContact = contactBlock(pcName, pcAddress, pcPhone, pcEmail);

  // The 15 Schedule 1 particulars in order, with their assembled values.
  const particulars: Particular[] = [
    {
      label: "1. Date of forwarding",
      scheduleText: "1. The date of forwarding the notice.",
      value: formatLong(dateForwarded),
      done: dateForwarded.trim().length > 0,
    },
    {
      label: "2. Site address / location",
      scheduleText: "2. The address of the construction site or precise description of its location.",
      value: siteAddress.trim(),
      done: siteAddress.trim().length > 0,
    },
    {
      label: "3. Local authority",
      scheduleText: "3. The name of the local authority where the construction site is located.",
      value: localAuthority.trim(),
      done: localAuthority.trim().length > 0,
    },
    {
      label: "4. Project & work description",
      scheduleText: "4. A brief description of the project and the construction work that it entails.",
      value: projectDescription.trim(),
      done: projectDescription.trim().length > 0,
    },
    {
      label: "5. Client contact details",
      scheduleText:
        "5. The following contact details of the client: name, address, telephone number and (if available) an email address.",
      value: clientContact,
      done: clientName.trim().length > 0 && clientAddress.trim().length > 0,
    },
    {
      label: "6. Principal designer contact details",
      scheduleText:
        "6. The following contact details of the principal designer: name, address, telephone number and (if available) an email address.",
      value: pdContact,
      done: pdName.trim().length > 0,
    },
    {
      label: "7. Principal contractor contact details",
      scheduleText:
        "7. The following contact details of the principal contractor: name, address, telephone number and (if available) an email address.",
      value: pcContact,
      done: pcName.trim().length > 0,
    },
    {
      label: "8. Planned start of construction",
      scheduleText: "8. The date planned for the start of the construction phase.",
      value: formatLong(startDate),
      done: startDate.trim().length > 0,
    },
    {
      label: "9. Time allocated for the work",
      scheduleText: "9. The time allocated by the client under regulation 4(1) for the construction work.",
      value: timeAllocated.trim(),
      done: timeAllocated.trim().length > 0,
    },
    {
      label: "10. Planned duration",
      scheduleText: "10. The planned duration of the construction phase.",
      value: duration.trim(),
      done: duration.trim().length > 0,
    },
    {
      label: "11. Estimated max people on site",
      scheduleText: "11. The estimated maximum number of people at work on the construction site.",
      value: maxPeople.trim(),
      done: maxPeople.trim().length > 0,
    },
    {
      label: "12. Planned number of contractors",
      scheduleText: "12. The planned number of contractors on the construction site.",
      value: numContractors.trim(),
      done: numContractors.trim().length > 0,
    },
    {
      label: "13. Contractors already appointed",
      scheduleText: "13. The name and address of any contractor already appointed.",
      value: appointedContractors.trim() || "None appointed yet",
      done: appointedContractors.trim().length > 0,
    },
    {
      label: "14. Designers already appointed",
      scheduleText: "14. The name and address of any designer already appointed.",
      value: appointedDesigners.trim() || "None appointed yet",
      done: appointedDesigners.trim().length > 0,
    },
    {
      label: "15. Client declaration",
      scheduleText:
        "15. A declaration signed by or on behalf of the client that the client is aware of the client duties under these Regulations.",
      value: clientDeclared
        ? "Confirmed: the client is aware of the client duties under CDM 2015. (Sign in the F10 / on the printed draft.)"
        : "",
      done: clientDeclared,
    },
  ];

  // Items 13 and 14 are answerable with "none appointed" — treat them as done
  // for the completion panel once everything else is, so the panel isn't stuck.
  const requiredItems: { label: string; done: boolean }[] = particulars.map((p) => {
    if (p.label.startsWith("13.") || p.label.startsWith("14.")) {
      return { label: p.label, done: true };
    }
    return { label: p.label, done: p.done };
  });
  const missingRequired = requiredItems.filter((i) => !i.done).map((i) => i.label);

  // Funnel: fire once the draft has meaningful content (a site + a description).
  const hasContent = siteAddress.trim().length > 0 && projectDescription.trim().length > 0;
  useEffect(() => {
    if (hasContent && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("f10-notification-drafter", { notifiable });
    }
  }, [hasContent, notifiable]);

  // Build the "copy all" plain-text draft.
  const buildAllText = () =>
    [
      "F10 NOTIFICATION DRAFT — CDM 2015 Schedule 1 particulars",
      "(Paste into HSE's online F10 service. The notification is made to HSE — this only drafts it.)",
      "",
      ...particulars.map(
        (p) => `${p.scheduleText}\n${p.value || "(not yet entered)"}`,
      ),
    ].join("\n\n");

  return (
    <div className="space-y-6">
      {/* Sticky progress + next gap. */}
      <div className="sticky top-20 z-10">
        <CompletionPanel items={requiredItems} title="F10 particulars" />
      </div>

      {/* ── Notifiability test ─────────────────────────────────────────────── */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">First: is this project F10 notifiable?</h2>
        <p className="mt-1 text-sm text-muted">
          Only notifiable projects need an F10. A project is notifiable under CDM 2015 regulation 6 if the
          construction work is scheduled to last <strong>longer than 30 working days with more than 20 workers
          on site at the same time</strong>, <em>or</em> to exceed <strong>500 person-days</strong> in total.
          Either one on its own triggers notification.
        </p>
        <div className="mt-4 space-y-2.5">
          <CheckboxRow checked={bigOver30} onChange={setBigOver30}>
            The work is scheduled to last <strong>longer than 30 working days</strong> AND have{" "}
            <strong>more than 20 workers</strong> working at the same time at any point.
          </CheckboxRow>
          <CheckboxRow checked={over500} onChange={setOver500}>
            The work is scheduled to exceed <strong>500 person-days</strong> in total (workers × days).
          </CheckboxRow>
        </div>

        {/* Live person-days helper. */}
        <div className="mt-4 rounded-lg border border-line bg-mist/60 p-4">
          <div className="text-[0.85rem] font-semibold text-ink">Not sure about person-days? Work it out:</div>
          <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <Field label="Average workers on site">
              <NumberInput min={0} step={1} value={pdWorkers} onChange={(e) => setPdWorkers(e.target.value)} placeholder="e.g. 6" />
            </Field>
            <Field label="Working days (total)">
              <NumberInput min={0} step={1} value={pdDays} onChange={(e) => setPdDays(e.target.value)} placeholder="e.g. 40" />
            </Field>
            <div className="pb-1 text-[0.95rem]" aria-live="polite">
              {personDays !== null ? (
                <span className={personDays > 500 ? "font-bold text-[#b34e10]" : "font-bold text-ink"}>
                  = {personDays.toLocaleString("en-GB")} person-days
                </span>
              ) : (
                <span className="text-muted">= person-days</span>
              )}
            </div>
          </div>
          {personDays !== null && personDays > 500 && !over500 && (
            <p className="mt-2 text-[0.82rem] font-semibold text-[#b34e10]">
              Over 500 — tick the person-days box above. A short job with a big crew can still be notifiable
              (19 workers × 29 days = 551 person-days).
            </p>
          )}
        </div>

        {/* Verdict */}
        {notifiable ? (
          <div className="mt-4 rounded-xl border border-risk-high/40 bg-[#fef0e6] px-5 py-4 text-[0.92rem] leading-relaxed text-[#b34e10]">
            <strong className="block font-display text-base">Likely F10 NOTIFIABLE — draft the notification below.</strong>
            This project meets the regulation 6 threshold, so it must be notified to HSE on form F10 before the
            construction phase starts. Assemble the Schedule 1 particulars below, then paste them into the
            official F10 service.
          </div>
        ) : (
          <div className="mt-4 rounded-xl border border-emerald/30 bg-emerald-tint px-5 py-4 text-[0.92rem] leading-relaxed text-emerald-deep">
            <strong className="block font-display text-base">Likely NOT F10 notifiable — no F10 needed.</strong>
            On what you have ticked this project does not meet the regulation 6 threshold, so no F10 is required.
            CDM 2015 still applies in full — every other CDM duty (appointments, construction phase plan,
            welfare) stands regardless of notification. Re-check if the programme grows. You can still draft the
            particulars below if you want them on record.
          </div>
        )}
      </div>

      {/* ── Where the notification is actually made ─────────────────────────── */}
      <div className="rounded-xl border border-emerald/30 bg-emerald-tint px-5 py-4 text-[0.92rem] leading-relaxed text-emerald-deep">
        <strong>The notification itself is made to HSE — this only drafts it.</strong> Assemble every Schedule 1
        particular here, then submit it on HSE&apos;s official online F10 service:{" "}
        <a href={F10_SERVICE_URL} rel="noopener" target="_blank" className="font-semibold underline">
          extranet.hse.gov.uk/lfserver/external/f10
        </a>
        . Nothing you enter here is sent anywhere — it stays in your browser.
      </div>

      {/* ── Particulars form ────────────────────────────────────────────────── */}
      {/* 1–4 The project */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">The project</h2>
        <div className="mt-4 grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="1. Date of forwarding the notice" hint="The date you submit the F10 to HSE.">
              <TextInput type="date" value={dateForwarded} onChange={(e) => setDateForwarded(e.target.value)} />
            </Field>
            <Field label="3. Local authority" hint="The council where the site is — HSE asks for the local authority.">
              <RememberedTextInput store="sites" value={localAuthority} onChange={(e) => setLocalAuthority(e.target.value)} placeholder="e.g. Leeds City Council" />
            </Field>
          </div>
          <Field label="2. Site address or precise location">
            <RememberedTextInput store="sites" value={siteAddress} onChange={(e) => setSiteAddress(e.target.value)} placeholder="Full site address or a precise description of where the work is" />
          </Field>
          <Field label="4. Brief description of the project & the construction work">
            <TextArea
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
              placeholder="e.g. New-build two-storey office: groundworks, steel frame, cladding, M&E fit-out and external works."
            />
          </Field>
        </div>
      </div>

      {/* 5 Client */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">5. Client</h2>
        <div className="mt-4 grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Client name">
              <RememberedTextInput store="people" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Person / organisation the work is for" />
            </Field>
            <Field label="Telephone">
              <TextInput value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} placeholder="Contact number" />
            </Field>
          </div>
          <Field label="Address">
            <TextInput value={clientAddress} onChange={(e) => setClientAddress(e.target.value)} placeholder="Client address" />
          </Field>
          <Field label="Email (if available)">
            <TextInput type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="Email address" />
          </Field>
        </div>
      </div>

      {/* 6 Principal designer */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">6. Principal designer</h2>
        <p className="mt-1 text-sm text-muted">
          Required where more than one contractor is (or will be) involved — appointed in writing by the client.
        </p>
        <div className="mt-4 grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name">
              <RememberedTextInput store="people" value={pdName} onChange={(e) => setPdName(e.target.value)} placeholder="Principal designer" />
            </Field>
            <Field label="Telephone">
              <TextInput value={pdPhone} onChange={(e) => setPdPhone(e.target.value)} placeholder="Contact number" />
            </Field>
          </div>
          <Field label="Address">
            <TextInput value={pdAddress} onChange={(e) => setPdAddress(e.target.value)} placeholder="Principal designer address" />
          </Field>
          <Field label="Email (if available)">
            <TextInput type="email" value={pdEmail} onChange={(e) => setPdEmail(e.target.value)} placeholder="Email address" />
          </Field>
        </div>
      </div>

      {/* 7 Principal contractor */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">7. Principal contractor</h2>
        <p className="mt-1 text-sm text-muted">
          Required where more than one contractor is (or will be) involved — appointed in writing by the client.
        </p>
        <div className="mt-4 grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name">
              <RememberedTextInput store="people" value={pcName} onChange={(e) => setPcName(e.target.value)} placeholder="Principal contractor" />
            </Field>
            <Field label="Telephone">
              <TextInput value={pcPhone} onChange={(e) => setPcPhone(e.target.value)} placeholder="Contact number" />
            </Field>
          </div>
          <Field label="Address">
            <TextInput value={pcAddress} onChange={(e) => setPcAddress(e.target.value)} placeholder="Principal contractor address" />
          </Field>
          <Field label="Email (if available)">
            <TextInput type="email" value={pcEmail} onChange={(e) => setPcEmail(e.target.value)} placeholder="Email address" />
          </Field>
        </div>
      </div>

      {/* 8–12 Programme & scale */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Programme &amp; scale</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="8. Planned start of the construction phase">
            <TextInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label="10. Planned duration of the construction phase">
            <TextInput value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="e.g. 14 weeks" />
          </Field>
          <Field
            label="9. Time allocated by the client for the work"
            hint="The time the client has allowed for the construction work (reg 4(1)) — often the same as the planned duration."
          >
            <TextInput value={timeAllocated} onChange={(e) => setTimeAllocated(e.target.value)} placeholder="e.g. 14 weeks from 1 September" />
          </Field>
          <Field label="11. Estimated max people at work on site">
            <TextInput value={maxPeople} onChange={(e) => setMaxPeople(e.target.value)} placeholder="e.g. 25 at peak" />
          </Field>
          <Field label="12. Planned number of contractors">
            <TextInput value={numContractors} onChange={(e) => setNumContractors(e.target.value)} placeholder="e.g. 8" />
          </Field>
        </div>
      </div>

      {/* 13–14 Already appointed */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Already appointed</h2>
        <p className="mt-1 text-sm text-muted">
          Name and address of anyone already appointed. Leave blank if none are appointed yet — that&apos;s a
          valid answer, and the draft will say so.
        </p>
        <div className="mt-4 grid gap-4">
          <Field label="13. Contractors already appointed (name & address)">
            <TextArea value={appointedContractors} onChange={(e) => setAppointedContractors(e.target.value)} placeholder="One per line — name and address. Leave blank if none yet." />
          </Field>
          <Field label="14. Designers already appointed (name & address)">
            <TextArea value={appointedDesigners} onChange={(e) => setAppointedDesigners(e.target.value)} placeholder="One per line — name and address. Leave blank if none yet." />
          </Field>
        </div>
      </div>

      {/* 15 Declaration */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">15. Client declaration</h2>
        <div className="mt-4 rounded-lg border border-line bg-mist/60 p-4">
          <CheckboxRow checked={clientDeclared} onChange={setClientDeclared}>
            The client is aware of the client duties under CDM 2015. (The F10 carries a declaration signed by or
            on behalf of the client — confirm this is true before submitting, and sign it on the F10.)
          </CheckboxRow>
        </div>
      </div>

      <RememberedNamesNote stores={["people", "sites"]} />

      {/* Actions */}
      <div className="flex flex-wrap items-center justify-end gap-3">
        <CopyButton tool="f10-notification-drafter" buildText={buildAllText} label="Copy all as text" />
        <GuardedPrintButton
          tool="f10-notification-drafter"
          label="Print / save draft as PDF"
          missing={missingRequired}
          warningTitle="This F10 draft still has missing particulars"
        />
      </div>

      {/* ── Printable / copyable draft ──────────────────────────────────────── */}
      <div className="print-area card shadow-soft p-6" id="f10-draft">
        <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
          F10 notification draft — CDM 2015 Schedule 1
        </div>
        <h2 className="mt-1 text-lg">
          {projectDescription.trim() ? projectDescription.trim() : "F10 notification draft"}
        </h2>
        <p className="mt-1 text-[0.82rem] text-muted">{siteAddress || "Site address not stated"}</p>

        <div
          className={`mt-3 rounded-lg px-4 py-3 text-[0.82rem] leading-relaxed ${
            notifiable
              ? "border border-risk-high/40 bg-[#fef0e6] text-[#b34e10]"
              : "border border-emerald/30 bg-emerald-tint text-emerald-deep"
          }`}
        >
          {notifiable
            ? "On the entered thresholds this project is F10 notifiable under CDM 2015 regulation 6. Submit these particulars to HSE on the official online F10 service before the construction phase starts."
            : "On the entered thresholds this project is NOT F10 notifiable. CDM 2015 still applies in full — these particulars are drafted for record only."}
        </div>

        <div className="mt-5 space-y-3">
          {particulars.map((p) => (
            <div key={p.label} className="border-t border-line/60 pt-3">
              <div className="text-[0.82rem] font-semibold text-ink">{p.scheduleText}</div>
              <p className="mt-0.5 whitespace-pre-line text-[0.9rem] leading-relaxed text-body">
                {p.value || <span className="text-muted">(not yet entered)</span>}
              </p>
            </div>
          ))}
        </div>

        <p className="mt-5 text-[0.78rem] leading-relaxed text-muted">
          This drafts the Schedule 1 particulars of a CDM 2015 F10 notification; it does not submit anything and
          does not decide notifiability for you — that is a judgement for the responsible dutyholder. The
          notification is made to HSE on the official online F10 service. Schedule 1 wording is from the
          Construction (Design and Management) Regulations 2015.
        </p>

        <PrintFooter toolName="F10 notification drafter" />
      </div>
    </div>
  );
}
